import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Clock } from 'lucide-react';
import { useTranslation } from '../i18n';
import apiClient from '../utils/api';

const SubscriptionBanner = ({ subscriptionData: initialData }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const hasFetched = useRef(false);

  console.log('SubscriptionBanner render:', { 
    pathname: location.pathname, 
    hasInitialData: !!initialData,
    initialData: initialData,
    subscription: subscription,
    loading 
  });

  const fetchSubscription = async () => {
    try {
      const response = await apiClient.get('/user/setup-status');
      if (response.data.subscription) {
        setSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('SubscriptionBanner useEffect:', { 
      pathname: location.pathname,
      hasInitialData: !!initialData,
      hasFetched: hasFetched.current
    });
    
    // Don't fetch on config page
    if (location.pathname === '/config') {
      setLoading(false);
      return;
    }
    
    // If we have initial data, use it
    if (initialData) {
      setSubscription(initialData);
      setLoading(false);
      return;
    }
    
    // Only fetch once if no initial data
    if (!hasFetched.current) {
      hasFetched.current = true;
      console.log('Fetching subscription for banner');
      fetchSubscription();
    }
  }, []); // Empty dependency array - fetch only once
  
  // Don't render on config page at all
  // Don't render on config page at all
  if (location.pathname === '/config') {
    console.log('On config page, returning null');
    return null;
  }

  if (loading || !subscription) return null;

  // Show banner for trial or inactive subscriptions
  const showBanner = subscription.status === 'trial' || 
                     subscription.status === 'canceled' || 
                     subscription.status === 'expired' ||
                     subscription.status === 'past_due' ||
                     subscription.status === 'incomplete';

  if (!showBanner) return null;

  const getBannerConfig = () => {
    console.log('Banner subscription data:', subscription);
    if (subscription.status === 'trial') {
      return {
        icon: Clock,
        bgColor: '#f0f9ff',
        borderColor: '#93c5fd',
        textColor: '#1e3a8a',
        title: t('subscription.trialEnding'),
        message: subscription.days_remaining !== null && subscription.days_remaining !== undefined
          ? t('subscription.trialRemaining', { days: subscription.days_remaining })
          : t('subscription.trialActive'),
        ctaText: t('subscription.upgradeToPro'),
        ctaUrl: subscription.buy_url
      };
    }

    // Expired, canceled, past_due, or incomplete
    return {
      icon: AlertCircle,
      bgColor: '#fee2e2',
      borderColor: '#ef4444',
      textColor: '#991b1b',
      title: t('subscription.subscriptionInactive'),
      message: t('subscription.reactivateMessage'),
      ctaText: t('subscription.reactivate'),
      ctaUrl: subscription.buy_url
    };
  };

  const config = getBannerConfig();
  const Icon = config.icon;

  return (
    <div style={{
      background: config.bgColor,
      borderBottom: `1px solid ${config.borderColor}`,
      padding: '0.5rem 1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap',
      fontSize: '0.875rem'
    }}>
      <Icon size={18} color={config.borderColor} />
      <div style={{ flex: '1', minWidth: '200px' }}>
        <span style={{ fontWeight: '500', color: config.textColor }}>
          {config.title}
        </span>
        <span style={{ color: config.textColor, marginLeft: '0.5rem' }}>
          {config.message}
        </span>
      </div>
      <button
        onClick={() => navigate('/config')}
        style={{
          padding: '0.375rem 1rem',
          background: config.borderColor,
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'opacity 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
      >
        {config.ctaText}
      </button>
    </div>
  );
};

export default SubscriptionBanner;
